/**
 * Checklist status machine.
 * Mirror sibling: functions/src/deltaProcessor.ts (lines 553-596)
 */

import type {
  ChecklistItem,
  ChecklistWorkflowStatus,
  UserRole,
  WorkspaceMember,
} from '../types/schema'
import { isWorkspaceRole, roleCanPerform } from '../utils/workspacePermissions'
import {
  type ChecklistBlockerCode,
  type BlockerCtaSection,
  BLOCKER_MESSAGES,
  ctaSectionFor,
} from '../types/checklistBlockerCodes'

export type StatusTransitionKind =
  | 'FREE'
  | 'AUTO'
  | 'GATED'
  | 'ADMIN_FORCE'
  | 'AUTO+FREE'
  | 'AUTO+GATED'

export type BlockerCode = ChecklistBlockerCode
export type { ChecklistBlockerCode, BlockerCtaSection }

export type AutoTriggerCode = 'ASSIGNEE_SET' | 'PROOF_UPLOADED' | 'PROOF_REMOVED'

export type ChecklistStatusLane =
  | 'UNSCHEDULED'
  | 'TODO'
  | 'IN_PROGRESS'
  | 'ASSESSING'
  | 'POSTPONED'
  | 'COMPLETE'

export interface StatusMeta {
  status: ChecklistWorkflowStatus
  lane: ChecklistStatusLane
  kind: StatusTransitionKind
  requires: ChecklistBlockerCode[]
  autoTriggers?: AutoTriggerCode[]
}

export interface BlockerEntry {
  code: ChecklistBlockerCode
  message: string
  ctaSection: BlockerCtaSection
}

export interface BlockerEvaluation {
  ok: boolean
  blockers: BlockerEntry[]
}

export interface StatusEvalActor {
  uid?: string | null
  id?: string | null
  userId?: string | null
  role?: UserRole | string | null
}

export type StatusEvalItem = Partial<ChecklistItem> & {
  dependencyAlertNodeIds?: string[]
  invalidDependencyNodeIds?: string[]
}

export const STATUS_META: Record<ChecklistWorkflowStatus, StatusMeta> = {
  CREATED: {
    status: 'CREATED',
    lane: 'TODO',
    kind: 'FREE',
    requires: [],
  },
  ASSIGNED: {
    status: 'ASSIGNED',
    lane: 'TODO',
    kind: 'AUTO+FREE',
    requires: ['ASSIGNEE_REQUIRED'],
    autoTriggers: ['ASSIGNEE_SET'],
  },
  ACCEPTED: {
    status: 'ACCEPTED',
    lane: 'TODO',
    kind: 'FREE',
    requires: [],
  },
  ASSESSING: {
    status: 'ASSESSING',
    lane: 'ASSESSING',
    kind: 'AUTO+GATED',
    requires: ['PROOF_REQUIRED'],
    autoTriggers: ['PROOF_UPLOADED'],
  },
  ONGOING: {
    status: 'ONGOING',
    lane: 'IN_PROGRESS',
    kind: 'GATED',
    requires: ['RESOURCES_UNVERIFIED'],
  },
  POSTPONED: {
    status: 'POSTPONED',
    lane: 'POSTPONED',
    kind: 'FREE',
    requires: [],
  },
  COMPLETED: {
    status: 'COMPLETED',
    lane: 'COMPLETE',
    kind: 'GATED',
    requires: [
      'DEPENDENCY_INCOMPLETE',
      'PROOF_REQUIRED',
      'LOCATION_OUT_OF_RANGE',
      'PROOF_APPROVER_REQUIRED',
      'RESOURCES_UNVERIFIED',
    ],
  },
  NOT_REQUIRED: {
    status: 'NOT_REQUIRED',
    lane: 'COMPLETE',
    kind: 'ADMIN_FORCE',
    requires: [],
  },
}

export function getStatusMeta(status: ChecklistWorkflowStatus): StatusMeta {
  return STATUS_META[status]
}

const DROP_ALLOWED_LANES = new Set<ChecklistStatusLane>([
  'UNSCHEDULED',
  'TODO',
  'IN_PROGRESS',
  'POSTPONED',
])

export function isDropAllowedForLane(lane: ChecklistStatusLane | string): boolean {
  return DROP_ALLOWED_LANES.has(lane as ChecklistStatusLane)
}

export function evaluateBlockers(
  item: StatusEvalItem | null | undefined,
  actor: StatusEvalActor | WorkspaceMember | null | undefined,
  targetStatus: ChecklistWorkflowStatus,
): BlockerEvaluation {
  const blockers: BlockerEntry[] = []

  if (targetStatus === 'ASSESSING') {
    const hasProof = Boolean(item?.proofFileUrl || item?.proofSubmissionId)
    if (!hasProof) {
      blockers.push({
        code: 'PROOF_REQUIRED',
        message: BLOCKER_MESSAGES.PROOF_REQUIRED,
        ctaSection: ctaSectionFor('PROOF_REQUIRED'),
      })
    }
  } else if (targetStatus === 'COMPLETED') {
    const depAlerts = item?.dependencyAlertNodeIds ?? []
    const invDeps = item?.invalidDependencyNodeIds ?? []
    if (depAlerts.length > 0 || invDeps.length > 0) {
      blockers.push({
        code: 'DEPENDENCY_INCOMPLETE',
        message: BLOCKER_MESSAGES.DEPENDENCY_INCOMPLETE,
        ctaSection: ctaSectionFor('DEPENDENCY_INCOMPLETE'),
      })
    }

    const hasProof = Boolean(item?.proofFileUrl || item?.proofSubmissionId)
    if (item?.requiresProofFile === true && !hasProof) {
      blockers.push({
        code: 'PROOF_REQUIRED',
        message: BLOCKER_MESSAGES.PROOF_REQUIRED,
        ctaSection: ctaSectionFor('PROOF_REQUIRED'),
      })
    }

    const actorUid = actor?.uid ?? actor?.id ?? actor?.userId ?? null
    const approverId = item?.proofApproverId ?? null
    const reviewStatus = item?.proofReviewStatus ?? null
    if (approverId && approverId !== actorUid && reviewStatus !== 'APPROVED') {
      blockers.push({
        code: 'PROOF_APPROVER_REQUIRED',
        message: BLOCKER_MESSAGES.PROOF_APPROVER_REQUIRED,
        ctaSection: ctaSectionFor('PROOF_APPROVER_REQUIRED'),
      })
    }

    const locationVerified = Boolean(item?.verifiedCoordinates)
    if (item?.requiresLocationPin === true && !locationVerified) {
      blockers.push({
        code: 'LOCATION_OUT_OF_RANGE',
        message: BLOCKER_MESSAGES.LOCATION_OUT_OF_RANGE,
        ctaSection: ctaSectionFor('LOCATION_OUT_OF_RANGE'),
      })
    }
  } else if (targetStatus === 'ASSIGNED') {
    const hasAssignee = Boolean(item?.assigneeId || item?.assigneeName)
    if (!hasAssignee) {
      blockers.push({
        code: 'ASSIGNEE_REQUIRED',
        message: BLOCKER_MESSAGES.ASSIGNEE_REQUIRED,
        ctaSection: ctaSectionFor('ASSIGNEE_REQUIRED'),
      })
    }
  } else if (targetStatus === 'NOT_REQUIRED') {
    const role = actor?.role
    if (!isWorkspaceRole(role) || !roleCanPerform(role, 'ADMIN')) {
      blockers.push({
        code: 'ADMIN_ONLY',
        message: BLOCKER_MESSAGES.ADMIN_ONLY,
        ctaSection: ctaSectionFor('ADMIN_ONLY'),
      })
    }
  }

  return {
    ok: blockers.length === 0,
    blockers,
  }
}

export function canPickInDropdown(
  item: StatusEvalItem | null | undefined,
  actor: StatusEvalActor | WorkspaceMember | null | undefined,
  targetStatus: ChecklistWorkflowStatus,
): { ok: boolean; reason?: string } {
  if (targetStatus === 'NOT_REQUIRED') {
    const role = actor?.role
    if (!isWorkspaceRole(role) || !roleCanPerform(role, 'ADMIN')) {
      return { ok: false, reason: BLOCKER_MESSAGES.ADMIN_ONLY }
    }
  }

  if (targetStatus === 'ASSESSING') {
    const hasProof = Boolean(item?.proofFileUrl || item?.proofSubmissionId)
    const isAssessing = item?.workflowStatus === 'ASSESSING'
    if (!hasProof && !isAssessing) {
      return { ok: false, reason: BLOCKER_MESSAGES.PROOF_REQUIRED }
    }
  }

  if (targetStatus === 'COMPLETED') {
    const isAssessing = item?.workflowStatus === 'ASSESSING'
    const actorUid = actor?.uid ?? actor?.id ?? actor?.userId ?? null
    const approverOnlyGate = isAssessing && Boolean(item?.proofApproverId) && item?.proofApproverId !== actorUid
    if (approverOnlyGate) {
      return { ok: false, reason: BLOCKER_MESSAGES.PROOF_APPROVER_REQUIRED }
    }
  }

  return { ok: true }
}

const REASSIGN_RESET_STATUSES = new Set<ChecklistWorkflowStatus>([
  'CREATED',
  'ASSIGNED',
  'ACCEPTED',
  'ASSESSING',
])

export function nextStatusAfterAssignment(
  currentStatus: ChecklistWorkflowStatus | null | undefined,
  hasAssignee: boolean,
): ChecklistWorkflowStatus | null {
  if (!hasAssignee) return null
  if (!currentStatus) return 'ASSIGNED'
  if (REASSIGN_RESET_STATUSES.has(currentStatus)) return 'ASSIGNED'
  return null
}

const TODO_WORKFLOW_STATUSES = new Set<ChecklistWorkflowStatus>(['CREATED', 'ASSIGNED', 'ASSESSING', 'ACCEPTED'])
const IN_PROGRESS_WORKFLOW_STATUSES = new Set<ChecklistWorkflowStatus>(['ONGOING', 'POSTPONED'])
const COMPLETE_WORKFLOW_STATUSES = new Set<ChecklistWorkflowStatus>(['COMPLETED', 'NOT_REQUIRED'])

export const workflowStatusToChecklistBoardStatus = (
  status: ChecklistWorkflowStatus | null | undefined,
): ChecklistItem['boardStatus'] => {
  if (!status) return null
  if (TODO_WORKFLOW_STATUSES.has(status)) return 'TODO'
  if (IN_PROGRESS_WORKFLOW_STATUSES.has(status)) return 'IN_PROGRESS'
  if (COMPLETE_WORKFLOW_STATUSES.has(status)) return 'COMPLETE'
  return null
}

